import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The root `createSceneProcessor` synthesises for a `.glb` or `.gltf` PackedScene. It loads the
 * GLB through `useGlb(path)`, renders it as a `<primitive>`, and drives its clips
 * (ADR-0012) through a `THREE.AnimationMixer` rooted on the GLB object, since the clips bind to
 * the GLB's own node names.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useGlb } from '../../../resources/useGlb';
import { MissingResourcePlaceholder } from '../../components/MissingResourcePlaceholder';
import { useGlbRoot } from './GlbInstanceContext';
import { useGlbGeometryInstances } from './glbGeometryInstances';
import { frameMembers } from './glbFrameOverrides';
import { glbNodeLinks } from './glbNodeLinks';
import { useAnimatedGlbVisibility } from './useAnimatedGlbVisibility';
import { useUniqueNamePaths } from '../../useUniqueNames';
import { useInstancingNodeTransform } from './instancingNodeTransform';
import { applyGlbNodeOverrides, hiddenGlbFramePaths, hiddenGlbObjects, isApplicableGlbOverride, resolveGlbOverrideTarget, } from './glbNodeOverrides';
import { flattenGlbObjects, GLB_ANIMATION_PLAYER_NAME } from './glbHierarchy';
import { useAnimationTransport } from '../../contexts/AnimationTransportContext';
import { useNodePath } from '../../contexts/NodePathContext';
import { useOptionalSelection } from '../../contexts/SelectionContext';
import { usePlaybackLoop } from '../../animation/usePlaybackLoop';
import { applyLoopOverride, loopSettingsFor } from '../../animation/loopOverride';
import { importedLoopMode } from '../../../resources/formats/glb/importedLoopMode';
import { restoreSnapshot, snapshotPose, subtreeObjects, } from '../../animation/poseSnapshot';
import { useAnimationDriverMount } from '../../animation/useAnimationDriverMount';
import { joinPath } from '../../../utils/nodePath';
import { useSceneResources } from '../../SceneResourcesContext';
import { resolveMaterialSource } from '../../materials/materialSource';
import { GlbSurfaceMaterialOverride } from './GlbSurfaceMaterialOverride';
import { GlbAttachedChildren } from './GlbAttachedChildren';
import { useGlbLightExposure } from './useGlbLightExposure';
import { importMaterialPath } from '../../../resources/formats/glb/glbProcessing';
import { forEachSurfaceMaterial } from '../../../resources/formats/glb/surfaceMaterials';
/** One empty list for a GLB still loading, so each hook keyed on `entries` keeps its memo. */
const NO_ENTRIES = [];
export function GLBSceneRoot({ node, children }) {
    // createSceneProcessor fills `properties` as a Record, so cast through unknown.
    const props = node.properties;
    // The instancing scene's inline override children target nodes inside this GLB, by name, so
    // a baked translation is overridden as in Godot.
    const nodePath = useNodePath();
    const root = useGlbRoot(nodePath);
    // An instancing node loads its GLB itself, to order its own children, and hands it down. A GLB
    // opened as the scene loads its own, and an empty path requests nothing.
    const ownLoad = useGlb(root.glb ? undefined : props.glbPath);
    const result = root.glb ?? ownLoad;
    const { overrides } = root;
    const object = result.value;
    // One flattening of the clone, shared by selection, visibility, overrides and material slots.
    const entries = object ? flattenGlbObjects(object) : NO_ENTRIES;
    // The clone is per consumer and stable, so re-applying on a change of its inputs is enough.
    useMemo(() => {
        if (object)
            applyGlbNodeOverrides(object, overrides, entries);
    }, [object, overrides, entries]);
    // A `surface_material_override/0` needs an ExtResource resolved and a `.tres` loaded, which
    // the synchronous mutation above cannot do, so each one mounts its own slot component.
    const importMaterials = useGlbImportMaterials(object);
    const materialOverrides = useGlbMaterialOverrides(object, entries, overrides);
    useInstancingNodeTransform(object, root.rawProperties);
    useGlbLightExposure(object);
    const uniquePaths = useUniqueNamePaths(root.path);
    const links = useMemo(() => (object ? glbNodeLinks(object, entries, root, uniquePaths) : []), [object, entries, root, uniquePaths]);
    useGlbGeometryInstances(object, entries, root, links);
    // Registers each GLB object under the tree's relPath scheme, so the SceneTreeViewer can select
    // and hide it.
    const selection = useOptionalSelection();
    const registerNodeObject = selection?.registerNodeObject;
    const unregisterNodeObject = selection?.unregisterNodeObject;
    useEffect(() => {
        if (!registerNodeObject || !unregisterNodeObject || nodePath === null)
            return;
        for (const { relPath, object: obj } of entries) {
            registerNodeObject(joinPath(nodePath, relPath), obj);
        }
        return () => {
            for (const { relPath } of entries)
                unregisterNodeObject(joinPath(nodePath, relPath));
        };
    }, [entries, nodePath, registerNodeObject, unregisterNodeObject]);
    // `applyGlbNodeOverrides` writes `visible` during render, and the hidden-paths effect below then
    // assigns every entry's `visible`. Both write one field, so it resolves here, or an authored
    // `visible = false` is switched back on.
    const overrideHidden = useMemo(() => (object ? hiddenGlbObjects(object, entries, overrides) : new Set()), [object, entries, overrides]);
    const overrideHiddenFrames = useMemo(() => hiddenGlbFramePaths(entries, overrides), [entries, overrides]);
    // A pushed `visible` track writes the property an override writes, so it takes its place. A node
    // draws when its own value and the value of each Skeleton3D and BoneAttachment3D above it say so.
    const animatedVisible = useAnimatedGlbVisibility(entries, root.path);
    const frames = useMemo(() => frameMembers(entries), [entries]);
    const hiddenNodePaths = selection?.hiddenNodePaths;
    useEffect(() => {
        if (nodePath === null)
            return;
        const hiddenByFrame = new Set();
        for (const { frame, below } of frames) {
            const isShown = animatedVisible.get(frame.path) ?? !overrideHiddenFrames.has(frame.path);
            if (!isShown)
                for (const object of below)
                    hiddenByFrame.add(object);
        }
        for (const { relPath, object: obj } of entries) {
            const isShown = animatedVisible.get(obj) ?? !overrideHidden.has(obj);
            const isTreeHidden = hiddenNodePaths?.has(joinPath(nodePath, relPath)) ?? false;
            obj.visible = isShown && !isTreeHidden && !hiddenByFrame.has(obj);
        }
        for (const { frame } of frames)
            if (frame.group)
                frame.group.visible = !hiddenByFrame.has(frame.group);
    }, [entries, frames, nodePath, hiddenNodePaths, overrideHidden, overrideHiddenFrames, animatedVisible]);
    // As in Godot, a GLB's clips live on an `AnimationPlayer` child that `glbSceneRootChildren`
    // synthesises, so selecting that child, not the GLB root, activates the driver.
    const transport = useAnimationTransport();
    const selectedNodePath = selection?.selectedNodePath ?? null;
    const animationPlayerPath = nodePath !== null ? joinPath(nodePath, GLB_ANIMATION_PLAYER_NAME) : null;
    const isActive = animationPlayerPath !== null && animationPlayerPath === selectedNodePath;
    const { clips, durations } = useMemo(() => {
        const list = object?.animations ?? [];
        return {
            clips: list,
            durations: Object.fromEntries(list.map((c) => [c.name, c.duration])),
        };
    }, [object]);
    // A full-subtree snapshot (poseSnapshot.ts), since skeletal and blended clips touch any bone.
    // AnimationPlayer snapshots only its tracks. glTF clips come named for three's own binding.
    const snapshotRef = useRef([]);
    const restore = useCallback(() => restoreSnapshot(snapshotRef.current), []);
    const bind = useCallback(
    // Every target is in the glTF's own subtree, which arrives whole, so nothing is missing.
    () => ({ clips, targets: object ? subtreeObjects(object) : [], isStale: () => false }), [clips, object]);
    const { mixerRef, actionsRef } = useAnimationDriverMount({
        // Always passed, so the AnimationDriverRegistry entry exists whatever the selection, and an
        // AnimationTree whose `anim_player` resolves here can root its mixer. The hook gates the
        // mixer build on isActive.
        object: object ?? null,
        clips,
        bind,
        // The synthesised AnimationPlayer path, not the GLB root, so an AnimationTree resolving
        // `anim_player` here finds the object and its clips.
        nodePath: animationPlayerPath,
        isActive,
        durations,
        onMixerBuilt: useCallback((targets) => {
            snapshotRef.current = snapshotPose(targets);
        }, []),
        restore,
    });
    // An inactive driver is 'stopped', so it never touches the scene and restores the authored
    // pose when it loses selection.
    const effectiveState = isActive ? transport.playState : 'stopped';
    usePlaybackLoop({
        playState: effectiveState,
        selectedClip: isActive ? transport.selectedClip : null,
        transportTime: transport.time,
        speedScale: transport.playbackSpeed,
        mixerRef,
        actionsRef,
        // 'auto' keeps the loop mode Godot's importer gave the clip. The preview loop override can
        // force one clamped pass or a repeat.
        configureAction: (action) => applyLoopOverride(action, transport.loopOverride, loopSettingsFor(importedLoopMode(action.getClip()))),
        reconfigureKey: transport.loopOverride,
        reportTime: transport.reportTime,
        restore,
    });
    // `children` is the subtree the host scene parents under this root, as Godot parents an
    // instanced scene's extra nodes to its root node. Every branch renders it,
    // or a slow or missing .glb deletes those nodes too.
    if (result.status === 'unavailable') {
        return (_jsxs(_Fragment, { children: [_jsx(MissingResourcePlaceholder, { shape: "box" }), children] }));
    }
    if (result.status === 'pending' || !object) {
        return _jsx(_Fragment, { children: children });
    }
    // useGlb clones the Object3D per consumer, so the value mounts directly.
    return (_jsxs("primitive", { object: object, children: [importMaterials, materialOverrides, _jsx(GlbAttachedChildren, { links: links, root: root }), children] }));
}
/**
 * One `<GlbSurfaceMaterialOverride>` per `.tres` an **Import sidecar** remaps a surface to, over
 * the surfaces the GLB processor tagged with it. A node's own override takes precedence: its
 * material carries no tag, so a remap never replaces it.
 */
function useGlbImportMaterials(object) {
    return useMemo(() => {
        if (!object)
            return null;
        const paths = new Set();
        forEachSurfaceMaterial(object, (material) => {
            const path = importMaterialPath(material);
            if (path)
                paths.add(path);
        });
        if (paths.size === 0)
            return null;
        return (_jsx(_Fragment, { children: [...paths].map((path) => (_jsx(GlbSurfaceMaterialOverride, { target: object, source: { kind: 'file', path }, replaces: (material) => importMaterialPath(material) === path }, `import:${path}`))) }));
    }, [object]);
}
/**
 * One `<GlbSurfaceMaterialOverride>` per override node with a `surface_material_override/0`. The
 * path resolves through `resolveGlbOverrideTarget`, as the transform overrides do, so a Godot path and a
 * three path that disagree about importer-synthesised levels land on one mesh.
 */
function useGlbMaterialOverrides(object, entries, overrides) {
    const pools = useSceneResources();
    return useMemo(() => {
        if (!object)
            return null;
        const slots = [];
        for (const override of overrides) {
            const ref = override.rawProperties['surface_material_override/0'];
            if (!ref || !isApplicableGlbOverride(override))
                continue;
            // A grafted override's ids belong to the outer scene that authored it, not the sub-scene it
            // renders under, and its scope carries both pools.
            const source = resolveMaterialSource(ref, override.scope ?? pools);
            if (!source)
                continue;
            const target = resolveGlbOverrideTarget(object, entries, override);
            if (!target)
                continue;
            slots.push(_jsx(GlbSurfaceMaterialOverride, { target: target, source: source }, joinPath(override.instanceSubPath ?? '', override.name)));
        }
        return slots.length > 0 ? _jsx(_Fragment, { children: slots }) : null;
    }, [object, entries, overrides, pools]);
}
//# sourceMappingURL=Component.js.map